import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
} from "lucide-react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import "./coreSolutions.css";
import s1 from "../../../assets/home/s1.webp";
import s2 from "../../../assets/home/s2.webp";
import s4 from "../../../assets/home/s4.webp";
import s3 from "../../../assets/home/s3.webp";

gsap.registerPlugin(ScrollTrigger);

const infrastructureServices = [
  {
    id: "01",
    title: "AI Data Operations & Human-in-the-Loop",
    description:
      "Human-verified annotation across image, video, audio, text and multi-sensor data for computer vision and AI/ML models.",
    image: s1,
    imageAlt: "AI data annotation and image labelling",
    href: "/ai-infrastructure/ai-data-operations",
    className: "core-card--feature",
  },
  {
    id: "02",
    title: "Data Collection",
    description:
      "Ethical, large-scale multimodal data collection, including India-specific road and ADAS datasets.",
    image: s2,
    imageAlt: "Real-world road data collection",
    href: "/ai-infrastructure/data-collection",
    className: "core-card--collection",
  },
  {
    id: "03",
    title: "Sports Data & AI Solutions",
    description:
      "Expert sports data annotation, player tracking and match analysis that turn footage into AI insights.",
    image: s3,
    imageAlt: "Sports footage for sports data and AI solutions",
    href: "https://www.qualitasglobalsports.com/",
    external: true,
    className: "core-card--sports",
  },
  {
    id: "04",
    title: "Generative AI",
    description:
      "Human review, RLHF, safety and hallucination evaluation for generative AI and LLM outputs.",
    image: s4,
    imageAlt: "Generative AI evaluation workspace",
    href: "/ai-infrastructure/generative-ai",
    className: "core-card--generative",
  },
];

function ServiceCard({ service }) {
  const Card = service.external ? "a" : Link;
  const linkProps = service.external
    ? {
        href: service.href,
        target: "_blank",
        rel: "noopener noreferrer",
      }
    : { to: service.href };

  return (
    <Card
      {...linkProps}
      className={`core-card ${service.className}`}
      aria-label={`Explore ${service.title}`}
    >
      <div className="core-card__image-wrap">
        <img
          src={service.image}
          alt={service.imageAlt}
          className="core-card__image"
          loading="lazy"
          decoding="async"
          width="900"
          height="650"
        />
        <span className="core-card__image-shade" />
        <div className="core-card__overlay">
          <div className="core-card__copy">
            <h3>{service.title}</h3>
            {service.description && <p>{service.description}</p>}
          </div>
          <span className="core-card__link">
            View service
            <ArrowUpRight size={16} strokeWidth={1.8} />
          </span>
        </div>
        <span className="core-card__image-arrow">
          <ArrowUpRight size={20} strokeWidth={1.7} />
        </span>
      </div>
    </Card>
  );
}

export default function CoreSolutions() {
  const sectionRef = useRef(null);

 useEffect(() => {
  const section = sectionRef.current;
  if (!section) return;

  const mm = gsap.matchMedia();

  mm.add("(prefers-reduced-motion: no-preference)", () => {
    gsap.from(".core-intro__eyebrow", {
      autoAlpha: 0,
      duration: 0.6,
      ease: "power1.out",
      scrollTrigger: {
        trigger: ".core-intro",
        start: "top 85%",
        once: true,
      },
    });

    gsap.from(".core-intro__title .core-line", {
      autoAlpha: 0,
      duration: 0.7,
      stagger: 0.18,
      ease: "power1.out",
      scrollTrigger: {
        trigger: ".core-intro__title",
        start: "top 85%",
        once: true,
      },
    });

    gsap.from(".core-intro__description", {
      autoAlpha: 0,
      duration: 0.7,
      delay: 0.15,
      ease: "power1.out",
      scrollTrigger: {
        trigger: ".core-intro__description",
        start: "top 88%",
        once: true,
      },
    });

    gsap.from(".core-group .core-card", {
      autoAlpha: 0,
      duration: 0.6,
      stagger: 0.25,
      ease: "power1.out",
      clearProps: "opacity,visibility",
      scrollTrigger: {
        trigger: ".core-grid",
        start: "top 82%",
        once: true,
      },
    });
  }, section);

  document.fonts?.ready.then(() => ScrollTrigger.refresh());

  return () => mm.revert();
}, []);
  return (
    <section className="core-solutions" ref={sectionRef}>
      <div className="core-solutions__inner">
        <header className="core-intro">
          <div className="core-intro__main">
            <span className="core-intro__eyebrow">
              <span className="core-intro__dot" />
              SERVICES
            </span>

            <h2 className="core-intro__title">
              <span className="core-line-wrap">
                <span className="core-line">AI data annotation,</span>
              </span>
              <span className="core-line-wrap">
                <span className="core-line">powered by humans, </span>
              </span>
              <span className="core-line-wrap">
                <span className="core-line">
                  built to  <em>trust.</em>
                </span>
              </span>
            </h2>
          </div>

          <p className="core-intro__description">
            Every high-performing AI model is built on one foundation:
            accurately trained, high-quality data. Qualitas Global helps you
            build reliable, production-ready AI models faster and with
            confidence. Our human-in-the-loop workflows combine rapid ramp-up,
            rigorous multi-level quality assurance, and enterprise-grade data
            security, so your models are trained on data you can trust.
          </p>
        </header>

        <div className="core-group">
          <div className="core-group__heading">
            <div>
              <span className="core-group__eyebrow">AI SERVICES</span>
              <h3>AI Infrastructure Services</h3>
            </div>
          </div>

          <div className="core-grid">
            {infrastructureServices.map((service) => (
              <ServiceCard key={service.id} service={service} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
